import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { useWishlist } from '../context/WishlistContext';

function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      className={`theme-toggle ${isDark ? 'dark' : ''}`}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Light mode' : 'Dark mode'}
    >
      <div className="theme-toggle-thumb">
        {isDark ? '🌙' : '☀️'}
      </div>
    </button>
  );
}

export default function Navbar() {
  const { totalItems } = useCart();
  const { count: wishlistCount } = useWishlist();
  const { isDark } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cartBump, setCartBump] = useState(false);
  const location = useLocation();
  const prevTotalItems = useRef(totalItems);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const increased = totalItems > prevTotalItems.current;
    prevTotalItems.current = totalItems;
    if (increased) {
      setCartBump(true);
      const timer = setTimeout(() => setCartBump(false), 400);
      return () => clearTimeout(timer);
    }
  }, [totalItems]);

  useEffect(() => { setMenuOpen(false); }, [location]);

  const navLinks = [
    { to: '/shop', label: 'Shop' },
    { to: '/build-a-bouquet', label: 'Build a Bouquet' },
    { to: '/custom-order', label: 'Custom Order' },
    { to: '/about', label: 'About' },
    { to: '/contact', label: 'Contact' },
  ];

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + '/');

  return (
    <>
    <div style={{ background: 'var(--rose)', color: 'white', textAlign: 'center', fontSize: 12, padding: '8px 16px', fontWeight: 500, letterSpacing: 0.3 }}>
      🚚 Live courier rates at checkout — pick your speed, anywhere in India
    </div>
    <nav style={{
      position: 'sticky', top: 0, zIndex: 1000,
      background: scrolled ? 'var(--bg-nav-scrolled)' : 'var(--bg-nav)',
      backdropFilter: 'blur(16px)',
      borderBottom: scrolled ? '1px solid var(--border-color)' : '1px solid transparent',
      transition: 'all 0.3s ease',
    }}>
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 72 }}>

        {/* ── LOGO ── */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img
            src={isDark ? '/logo-dark.png' : '/logo-light.png'}
            alt="Bloomwish — wish it. bloom it."
            style={{
              height: 44,
              width: 'auto',
              objectFit: 'contain',
              transition: 'opacity 0.3s ease, height 0.3s ease',
              display: 'block',
            }}
          />
        </Link>

        {/* ── DESKTOP NAV ── */}
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }} className="desktop-nav">
          {navLinks.map(link => (
            <Link key={link.to} to={link.to} style={{
              padding: '8px 16px', borderRadius: 40, fontSize: 14,
              fontWeight: isActive(link.to) ? 500 : 400,
              color: isActive(link.to) ? 'var(--rose)' : 'var(--text-secondary)',
              background: isActive(link.to) ? 'var(--blush)' : 'transparent',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={e => { if (!isActive(link.to)) { e.target.style.background = 'var(--blush)'; e.target.style.color = 'var(--rose)'; }}}
            onMouseLeave={e => { if (!isActive(link.to)) { e.target.style.background = 'transparent'; e.target.style.color = 'var(--text-secondary)'; }}}
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* ── RIGHT ACTIONS ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Dark/Light Toggle */}
          <ThemeToggle />

          {/* Wishlist */}
          <Link to="/wishlist" style={{
            position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 44, height: 44, borderRadius: '50%',
            background: 'var(--blush)', color: 'var(--rose-dark)',
            transition: 'all 0.2s ease',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
            </svg>
            {wishlistCount > 0 && (
              <span style={{
                position: 'absolute', top: -2, right: -2,
                background: 'var(--rose)', color: 'white',
                fontSize: 10, fontWeight: 700,
                width: 18, height: 18, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid var(--bg-primary)',
              }}>{wishlistCount}</span>
            )}
          </Link>

          {/* Cart */}
          <Link to="/cart" className={cartBump ? 'cart-bump' : ''} style={{
            position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 44, height: 44, borderRadius: '50%',
            background: 'var(--blush)', color: 'var(--rose-dark)',
            transition: 'all 0.2s ease',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 01-8 0"/>
            </svg>
            {totalItems > 0 && (
              <span style={{
                position: 'absolute', top: -2, right: -2,
                background: 'var(--rose)', color: 'white',
                fontSize: 10, fontWeight: 700,
                width: 18, height: 18, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid var(--bg-primary)',
              }}>{totalItems}</span>
            )}
          </Link>

          {/* Hamburger */}
          <button onClick={() => setMenuOpen(!menuOpen)} className="hamburger"
            style={{ display: 'none', background: 'none', border: 'none', padding: 8, cursor: 'pointer', color: 'var(--text-primary)' }}
            aria-label="Menu"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {menuOpen
                ? <><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>
                : <><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></>
              }
            </svg>
          </button>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      {menuOpen && (
        <div style={{
          background: 'var(--bg-card)',
          borderTop: '1px solid var(--border-color)',
          padding: '16px 24px 24px',
        }}>
          {/* Logo in mobile menu */}
          <div style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--border-subtle)' }}>
            <img
              src={isDark ? '/logo-dark.png' : '/logo-light.png'}
              alt="Bloomwish"
              style={{ height: 36, width: 'auto' }}
            />
          </div>
          {navLinks.map(link => (
            <Link key={link.to} to={link.to} style={{
              display: 'block', padding: '13px 0',
              fontSize: 17, fontFamily: 'var(--font-display)',
              color: isActive(link.to) ? 'var(--rose)' : 'var(--text-primary)',
              borderBottom: '1px solid var(--border-subtle)',
            }}>
              {link.label}
            </Link>
          ))}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Dark Mode</span>
            <ThemeToggle />
          </div>
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: flex !important; }
        }
      `}</style>
    </nav>
    </>
  );
}
